import type { SelectItem } from '@mantine/core';
import { Center, Stack } from '@mantine/core';
import type { UserOut as IUser } from '@repo/api-codegen/v2';
import GroupIcon from '@repo/common/components/GroupIcon/GroupIcon';
import { Icon, MultiSelect, Title } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useExtendedUserList } from '../../../../api/hooks/user/useExtendedUserList';
import { getDisplayName } from '../../../../utils/userUtils';
import { getOwnerAndGroupSelectorOptions } from '../../../EntityPageLayout/utils';
import { UserAvatar } from '../../../UserAvatar';
import { useAddMonitorStoreContext } from '../hooks';

type UserItem = SelectItem & {
	group: 'Users';
	user: IUser;
};

type GroupItem = SelectItem & {
	group: 'Groups';
};

type EmailItem = SelectItem & {
	group: 'Emails';
};

type UserOrEmailItem = UserItem | EmailItem;
type OwnerItem = UserItem | GroupItem;

function isUserItem(item: UserOrEmailItem): item is UserItem {
	return item.group === 'Users';
}

function isGroupItem(item: OwnerItem): item is GroupItem {
	return item.group === 'Groups';
}

function ManageStack() {
	const store = useAddMonitorStoreContext();
	const { activeUsers, userGroups } = useExtendedUserList();

	// Owner selector with support for both users and groups
	const ownersData = useMemo(() => {
		const ownerOptions = getOwnerAndGroupSelectorOptions(
			activeUsers ?? [],
			userGroups ?? [],
			store.setOwners,
			store.setOwnersGroups
		);

		return ownerOptions.map((option) => ({
			value: option.value,
			label: option.label,
			group: option.group === 'users' ? 'Users' : 'Groups',
			user:
				option.group === 'users'
					? activeUsers?.find((u) => u.id === option.value)
					: undefined,
		})) as OwnerItem[];
	}, [activeUsers, userGroups, store.setOwners, store.setOwnersGroups]);

	// Subscriber selector allow for adding custom emails
	const subscribersData = useMemo(() => {
		const userItems =
			activeUsers?.map((u) => ({
				value: `user:${u.id}`,
				label: getDisplayName(u),
				group: 'Users',
				user: u,
			})) || [];
		const emailItems = store.tempEmailSubscribers.map((e) => ({
			value: `email:${e}`,
			label: e,
			group: 'Emails',
		}));
		return [...userItems, ...emailItems] as UserOrEmailItem[];
	}, [activeUsers, store.tempEmailSubscribers]);

	const [subscribersValue, setSubscribersValue] = useState<string[]>([]);
	const [subscriberError, setSubscriberError] = useState<string>('');

	const handleSetOwnersValue = (value: string[]) => {
		const activeUserIds = activeUsers?.map((user) => user.id) ?? [];
		const userGroupIds = userGroups?.map((group) => group.id) ?? [];

		const users = value.filter((id) => activeUserIds.includes(id));
		const groups = value.filter((id) => userGroupIds.includes(id));

		store.setOwners(users);
		store.setOwnersGroups(groups);
	};

	const handleSetSubscribersValue = (value: string[]) => {
		setSubscribersValue(value);
		setSubscriberError('');
		store.setEmailSubscribers(
			value
				.filter((v) => v.startsWith('email:'))
				.map((v) => v.slice('email:'.length))
		);
		store.setSubscribers(
			value
				.filter((v) => v.startsWith('user:'))
				.map((v) => v.slice('user:'.length))
		);
	};

	const handleAddEmail = useCallback(
		(email: string) => {
			// simple heuristic to check email syntax
			if (/\S+@\S+\.\S+/.test(email)) {
				store.setTempEmailSubscribers([...store.tempEmailSubscribers, email]);
				store.setEmailSubscribers([...store.emailSubscribers, email]);
				setSubscribersValue([...subscribersValue, `email:${email}`]);
				setSubscriberError('');
				return {
					value: `email:${email}`,
					label: email,
					group: 'Emails',
				};
			} else {
				setSubscriberError(`"${email}" is not a valid email`);
				return undefined;
			}
		},
		[store, subscribersValue]
	);

	return (
		<Stack p={0} spacing="lg">
			<Title size="md">Manage</Title>
			<MultiSelect<OwnerItem>
				label="Owners"
				placeholder="Select owners"
				data={ownersData}
				value={[...store.owners, ...store.ownersGroups]}
				setValue={handleSetOwnersValue}
				renderIcon={(item: OwnerItem) => (
					<Center p={2}>
						{isGroupItem(item) ? (
							<GroupIcon item={item} />
						) : (
							<UserAvatar user={item.user} size="xxs" />
						)}
					</Center>
				)}
				optional={false}
				error={store.ownersError}
				dropdownPosition="flip"
			/>
			<MultiSelect<UserOrEmailItem>
				label="Subscribers"
				placeholder="Select subscribers"
				data={subscribersData}
				value={subscribersValue}
				setValue={handleSetSubscribersValue}
				renderIcon={(item: UserOrEmailItem) =>
					isUserItem(item) ? (
						<Center p={2}>
							<UserAvatar user={item.user} size="xxs" />
						</Center>
					) : (
						<Icon name="user" />
					)
				}
				optional
				creatable
				getCreateLabel={(query) =>
					subscribersData?.filter((value) => value.label?.includes(query))
						.length > 0
						? ''
						: `Add "${query}" to subscribers`
				}
				onCreate={handleAddEmail}
				onClick={() => setSubscriberError('')}
				error={subscriberError}
				dropdownPosition="flip"
			/>
		</Stack>
	);
}

const ObservedManageStack = observer(ManageStack);
export default ObservedManageStack;
